// eslint-disable-next-line jira/import-whitelist
import { announcementBannerSettingsV4Resource } from '@atlassian/jira-admin-pages-system-ui-announcement-banner-resources/src/controllers/settings/resource/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { forgeProjectSettingsModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticJSWUserBoardSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-jsw-userboard-sidebar/index.tsx';
import { staticPlanSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-plan-sidebar/index.tsx';
import { staticProjectSettingsSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-settings-connect-addons/index.tsx';
import {
	sidebarNav4Resource,
	sidebarNav4UiStateResource,
} from '@atlassian/jira-navigation-apps-resources/src/services/sidebar-nav4/index.tsx';
import { topNav4UIStateResource } from '@atlassian/jira-navigation-apps-resources/src/services/top-nav/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { navigationPlansVisibilityResource } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { consentBannerUserPreferencesResource } from '@atlassian/jira-router-resources-cookie-consent/src/index.tsx';
import { checkApiKeyManagementKeysResource } from '@atlassian/jira-router-resources-eoc-api-key-management/src/services/index.tsx';
import { consolidationUserStateResource } from '@atlassian/jira-router-resources-eoc-og-consolidation-user-state/src/index.tsx';

import { getConsolidationStateResource } from '@atlassian/jira-router-resources-eoc-tenant-info/src/services/index.tsx';

import { CONSOLIDATION_STATE_RESOURCE_TYPE } from '@atlassian/jira-router-resources-eoc-tenant-info/src/services/constants.tsx';
import { gicSmartFieldsFeatureResource } from '@atlassian/jira-router-resources-gic-smart-fields/src/services/index.tsx';
import { gicOptInStatusResources } from '@atlassian/jira-router-resources-global-issue-create-switch/src/index.tsx';
import { incomingMailServerExpiryStatusResource } from '@atlassian/jira-router-resources-incoming-mail-expiry-status/src/services/index.tsx';
import { issueTransitionChangeboardingPreferencesResource } from '@atlassian/jira-router-resources-issue-transition-changeboarding-preferences/src/services/index.tsx';
import { issueTransitionOptInStatusResource } from '@atlassian/jira-router-resources-issue-transition-labs-opt-in/src/services/index.tsx';
import { navigationSidebarAutomationLegacyEnabledResource } from '@atlassian/jira-router-resources-navigation-sidebar-automation/src/index.tsx';
import { navigationSidebarGlobalResource } from '@atlassian/jira-router-resources-navigation-sidebar-global/src/index.tsx';
import { navigationUiStateResource } from '@atlassian/jira-router-resources-navigation-ui-state/src/index.tsx';
import { newIssueViewTransitionLockInStatusResource } from '@atlassian/jira-router-resources-new-issue-view-transition-lock-in-status/src/services/index.tsx';
import { persistentIssueCreateModalViewResource } from '@atlassian/jira-router-resources-persistent-issue-create-modal-view/src/controllers/resource/index.tsx';
import { projectContextResourceWithCache } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { fetchSiteTraitsResource } from '@atlassian/jira-router-resources-recommendations/src/services/fetch-site-traits/index.tsx';
import { fetchUserTraitsResource } from '@atlassian/jira-router-resources-recommendations/src/services/fetch-user-traits/index.tsx';
import { jpdRecommendationDismissedResource } from '@atlassian/jira-router-resources-recommendations/src/services/jpd-recommendation-dismissed-resource/index.tsx';
import { rovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { themePreferenceResource } from '@atlassian/jira-router-resources-theme-preference/src/controllers/resource-erai-1615-old/index.tsx';
import { userHomepageResource } from '@atlassian/jira-router-resources-user-homepage/src/services/index.tsx';
import {
	resourceWithCondition,
	resourceWithCondition2,
} from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { postOfficeJiraSideNavResource } from '@atlassian/jira-router-resources-post-office/src/services/index.tsx';
import { createResource } from '@atlassian/react-resource-router';
import type { RouteResource } from '@atlassian/react-resource-router/src/common/types.tsx';

const inExperimentCohortJiraSideNavMSRExperiment = () => {
	// We need to use UNSAFE_noExposureExp to check if jira-side-nav experiment is enabled without firing exposure events
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [sideNavExpConfig] = UNSAFE_noExposureExp('post_office_jira_side_nav_exp_msr_vs_no_rec');

	return sideNavExpConfig.get('isEnabled', false);
};

const conditionalPostOfficeJiraSideNavResource = resourceWithCondition2(
	() =>
		!getWillShowNav4() &&
		fg('post_office_ssr_jira_side_nav_enabled') &&
		inExperimentCohortJiraSideNavMSRExperiment(),
	postOfficeJiraSideNavResource,
);

const isModernIssueTransitionSettingEnabled = () => {
	return expVal('jira_issue_transition_screen_modernisation_toggle', 'value', 'OFF') !== 'OFF';
};

const conditionalGicOptInStatusResource = resourceWithCondition(
	() => fg('render_modern_global_issue_create_modal_experience'),
	gicOptInStatusResources,
);

const conditionalIssueTransitionOptInStatusResource = resourceWithCondition(
	isModernIssueTransitionSettingEnabled,
	issueTransitionOptInStatusResource,
);

const conditionalGicSmartFieldsOptoutResource = resourceWithCondition2(
	() => fg('migrate_gic_smart_fields_jira_labs'),
	gicSmartFieldsFeatureResource,
);

export const getModernIssueTransitionResources = (): RouteResource[] => [
	conditionalIssueTransitionOptInStatusResource,
	issueTransitionChangeboardingPreferencesResource,
];

const conditionalOperationsJiraSideNavResource = resourceWithCondition2(
	() => getWillShowNav4() && fg('operations_ssr_jira_side_nav_enabled'),
	getConsolidationStateResource(),
	createResource({
		type: `${CONSOLIDATION_STATE_RESOURCE_TYPE}_NOOP`,
		getKey: () => `${CONSOLIDATION_STATE_RESOURCE_TYPE}_NOOP`,
		getData: async () => null,
		maxAge: Number.MAX_SAFE_INTEGER,
		isBrowserOnly: false,
	}),
);

// There is a best effort duplicate removal in the `getRoutes` function but it
// only works if the resources have the same instance. b/c resourceWithCondition2
// will create a new reference each time it is run, by keeping this outside the
// route factory, we can ensure that the duplicate removal works when it is used
// in multiple places.
const getDedupableConditionalResources = (isUsingSidebarEntrypoint?: boolean) => {
	const resources = [
		resourceWithCondition2(getWillShowNav4, sidebarNav4UiStateResource),
		resourceWithCondition2(getWillShowNav4, topNav4UIStateResource),
		conditionalOperationsJiraSideNavResource,
	];
	if (isUsingSidebarEntrypoint) {
		// We don't want sidebarNav4Resource loaded for entrypoints otherwise we end up with a client side api call
		// when we don't need it as SSR will have loaded the data already.
		return resources;
	}
	resources.push(resourceWithCondition2(getWillShowNav4, sidebarNav4Resource));
	return resources;
};

type getNavigationResourcesOptions = {
	isUsingSidebarEntrypoint?: boolean;
};

export const getNavigationResources = ({
	isUsingSidebarEntrypoint,
}: getNavigationResourcesOptions = {}): RouteResource[] => [
	navigationUiStateResource,
	navigationPlansVisibilityResource,
	userHomepageResource,
	conditionalGicOptInStatusResource,
	announcementBannerSettingsV4Resource,
	consentBannerUserPreferencesResource,
	consolidationUserStateResource,
	...getDedupableConditionalResources(isUsingSidebarEntrypoint),
	themePreferenceResource,
	...getModernIssueTransitionResources(),
	persistentIssueCreateModalViewResource,
	conditionalGicSmartFieldsOptoutResource,
	incomingMailServerExpiryStatusResource,
	rovoEntitlementResource,
	jpdRecommendationDismissedResource,
	fetchUserTraitsResource,
	fetchSiteTraitsResource,
	conditionalPostOfficeJiraSideNavResource,
];

export const getUserBoardNavigationResources = (): RouteResource[] => [
	staticJSWUserBoardSidebarNavigationResource,
];

export const getNavigationSidebarPlanResources = (): RouteResource[] => [
	staticPlanSidebarNavigationResource,
];

export const getNavigationSidebarProductsSettingResource = (): RouteResource[] => [
	navigationSidebarGlobalResource,
	getConsolidationStateResource(),
	checkApiKeyManagementKeysResource(),
];

export const getNavigationSidebarProjectSettingsResource = (): RouteResource[] => [
	newIssueViewTransitionLockInStatusResource,
	navigationSidebarAutomationLegacyEnabledResource,
	projectContextResourceWithCache,
	forgeProjectSettingsModuleResource,
	staticProjectSettingsSidebarConnectAddonsNavigationResource,
];
